import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Phone, Mail, Clock, Facebook, Twitter, Instagram, Youtube, Cookie } from 'lucide-react';
import CookieConsentModal from '@/components/guest/CookieConsentModal';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { APP_VERSION, ENVIRONMENT, isProduction } from '@/lib/version';
import { ORDER_SITE_URL } from '@/lib/externalLinks';

const CelebrationFooter = () => {
  const { openSettings, showModal, setShowModal, acceptAll, acceptEssential, customizeSettings } = useCookieConsent();

  return (
    <footer className="bg-gray-900 text-white relative overflow-hidden">

      {/* Main Footer */}
      <section className="py-12 sm:py-16">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            {/* Company Info */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <img src="/images/dmf_logo_optimized.webp" alt="Dough Man Foods" className="h-10 w-10"
                     onError={(e) => { e.currentTarget.src = "/images/dmf_logo.png"; }} />
                <span className="font-bold text-2xl text-white">Dough Man</span>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Making Ghana's invisible moments visible, one celebration at a time. From that first 'yes' to surviving another Accra week - every win deserves its moment.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-amber-600 transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-amber-600 transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-amber-600 transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-amber-600 transition-colors">
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-lg mb-6">Celebrate</h4>
              <ul className="space-y-3">
                <li><Link to="/menu" className="text-gray-400 hover:text-white transition-colors">Menu</Link></li>
                <li><Link to="/doughnut-menu" className="text-gray-400 hover:text-white transition-colors">Full Pricelist</Link></li>
                <li><Link to="/how-it-works" className="text-gray-400 hover:text-white transition-colors">How It Works</Link></li>
                <li><Link to="/rewards" className="text-gray-400 hover:text-white transition-colors">Rewards Program</Link></li>
                <li><a href={ORDER_SITE_URL} className="text-gray-400 hover:text-white transition-colors">Order Online</a></li>
                <li><Link to="/delivery-zones" className="text-gray-400 hover:text-white transition-colors">Delivery Areas</Link></li>
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h4 className="font-semibold text-lg mb-6">Connect</h4>
              <ul className="space-y-3">
                <li><Link to="/reviews" className="text-gray-400 hover:text-white transition-colors">Customer Reviews</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">Our Story</Link></li>
                <li><Link to="/resources" className="text-gray-400 hover:text-white transition-colors">Free Resources</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact Us</Link></li>
                <li><a href={ORDER_SITE_URL} className="text-gray-400 hover:text-white transition-colors">Corporate Orders</a></li>
                <li><a href={ORDER_SITE_URL} className="text-gray-400 hover:text-white transition-colors">Custom Packages</a></li>
                <li><Link to="/developers" className="text-gray-400 hover:text-white transition-colors">Developers API</Link></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold text-lg mb-6">Contact Info</h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-gray-400 text-sm">
                    <div className="mb-2">
                      <strong className="text-gray-300">North Kaneshie:</strong><br />
                      Aduemi Street,<br />
                      North Kaneshie, Accra
                    </div>
                    <div>
                      <strong className="text-gray-300">Dansoman:</strong><br />
                      <a href="https://maps.app.goo.gl/UsmFHE2C9srkFsAA9" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                        Get Directions
                      </a>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-gray-400 text-sm space-y-1">
                    <div>
                      <span className="text-gray-300">North Kaneshie:</span><br />
                      <a href="tel:+233201100533" className="block hover:text-white transition-colors">
                        +233 201 100 533
                      </a>
                    </div>
                    <div className="mt-2">
                      <span className="text-gray-300">Dansoman:</span><br />
                      <a href="tel:+233274010329" className="block hover:text-white transition-colors">
                        +233 274 010 329
                      </a>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-amber-600" />
                  <a href="mailto:taste@doughmanfoods.com" className="text-gray-400 hover:text-white transition-colors text-sm">
                    taste@doughmanfoods.com
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-gray-400 text-sm">
                    Monday to Friday: 8:00 AM - 5:00 PM<br />
                    Saturday: 9:00 AM - 3:00 PM<br />
                    Sunday: Closed<br />
                    <span className="text-xs text-gray-500 mt-1 block">
                      Holidays: Open until 3:00 PM
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Features Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-8 border-t border-gray-800">
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">🚚</span>
              </div>
              <h5 className="font-medium mb-1">Express Delivery</h5>
              <p className="text-xs text-gray-400">30-min delivery guarantee</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">🕐</span>
              </div>
              <h5 className="font-medium mb-1">Kitchen Timestamp</h5>
              <p className="text-xs text-gray-400">See when yours was made</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">💬</span>
              </div>
              <h5 className="font-medium mb-1">WhatsApp Ready</h5>
              <p className="text-xs text-gray-400">Order in seconds, not minutes</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">🏆</span>
              </div>
              <h5 className="font-medium mb-1">Celebration Rewards</h5>
              <p className="text-xs text-gray-400">Earn points with every order</p>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="border-t border-gray-800 pt-8 mb-8">
            <h5 className="font-medium mb-4 text-center">We Accept</h5>
            <div className="flex justify-center items-center gap-6 flex-wrap">
              <div className="bg-gray-800 rounded-lg px-4 py-2 text-sm">Visa</div>
              <div className="bg-gray-800 rounded-lg px-4 py-2 text-sm">Mastercard</div>
              <div className="bg-gray-800 rounded-lg px-4 py-2 text-sm">MTN Mobile Money</div>
              <div className="bg-gray-800 rounded-lg px-4 py-2 text-sm">Vodafone Cash</div>
              <div className="bg-gray-800 rounded-lg px-4 py-2 text-sm">AirtelTigo Money</div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8 text-center">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} Dough Man Foods. All rights reserved.
              </p>
              <div className="flex gap-6 text-sm">
                <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
                <button
                  onClick={openSettings}
                  className="text-gray-400 hover:text-white transition-colors flex items-center gap-1"
                >
                  <Cookie className="w-3 h-3" />
                  Cookie Settings
                </button>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2 mt-4">
              <p className="text-gray-500 text-xs">
                Life's too short not to celebrate - make memories with Dough Man 🍩
              </p>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-600">v{APP_VERSION}</span>
                {!isProduction() && (
                  <Badge variant="outline" className="text-xs py-0 px-1 h-4 bg-gray-800 text-gray-400 border-gray-700">
                    {ENVIRONMENT}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Large Background Text */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none">
        <div className="text-[8rem] sm:text-[12rem] lg:text-[20rem] font-bold text-gray-800/5 text-center leading-none select-none">
          Dough Man
        </div>
      </div>
      {/* Cookie Consent Modal */}
      <CookieConsentModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onAcceptAll={acceptAll}
        onAcceptEssential={acceptEssential}
        onCustomize={customizeSettings}
      />
    </footer>
  );
};

export default CelebrationFooter;
